import { Component, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TransactionService } from '../../services/transaction.service';
import { Transaction } from '../../models/transaction.model';
import { generatePagination } from '../../utils/pagination.utils';
import { DebtService, ReturnTransactionPayload } from '../../services/debt.service';
import { AuthService } from '../../services/auth.service';
import { UserService } from '../../services/user.service';

@Component({
  selector: 'app-bank-transactions-list',
  standalone: false,
  templateUrl: './bank-transactions-list.component.html',
  styleUrl: './bank-transactions-list.component.css',
})
export class BankTransactionsListComponent {
  @Input() uuid!: string;
  transactions: Transaction[] = [];
  nextPageUrl: string | null = null;
  previousPageUrl: string | null = null;
  currentPage = 1;
  totalPages = 1;
  pageNumbers: any = [];
  searchQuery: string = '';
  submitted = false;
  transactionType = '';
  transactionStatus = '';
  accountingType = '';
  paymentMethod = '';
  activeRfi = false;
  showRefundModal = false;
  isRefunding = false;
  showReturnModal = false;
  selectedTransaction: Transaction = new Transaction();
  isComplianceAdmin = false;
  isRefundsManager = false;

  showTransferModal = false;
  transferSourceWallet = '';
  transferAmount: number | null = null;
  transferReference = '';
  relatedTransactionUuid = '';
  relatedTransactionType = '';
  isTransferring = false;

  constructor(
    private transactionService: TransactionService,
    private debtService: DebtService,
    private authService: AuthService,
    private userService: UserService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit() {
    this.searchQuery = this.uuid;
    const params = this.route.snapshot.queryParamMap;
    const pageParam = params.get('page');
    const parsedPage = pageParam ? Number(pageParam) : 1;
    this.currentPage = isNaN(parsedPage) || parsedPage < 1 ? 1 : parsedPage;

    this.transactionType = params.get('transactionType') ?? '';
    this.transactionStatus = params.get('transactionStatus') ?? '';
    this.accountingType = params.get('accountingType') ?? '';
    this.activeRfi =
      params.get('active_rfi') === 'true' ||
      params.get('rfi_received_at') === 'true';

    this.loadTransactions(this.currentPage);
    this.checkPrivileges();
  }

  private syncUrl(page: number = this.currentPage) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        page: page > 1 ? page : null,
        transactionType: this.transactionType || null,
        transactionStatus: this.transactionStatus || null,
        accountingType: this.accountingType || null,
        active_rfi: this.activeRfi ? 'true' : null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  reset() {
    this.transactions = [];
    this.nextPageUrl = null;
    this.previousPageUrl = null;
    this.totalPages = 1;
    this.pageNumbers = [];
  }

  loadTransactions(page: number) {
    this.reset();
    this.submitted = true;
    this.currentPage = page;
    this.syncUrl(page);

    this.transactionService
      .getTransactions(
        page,
        this.searchQuery,
        this.transactionType,
        this.transactionStatus,
        this.accountingType,
        false,
        this.activeRfi,
        this.paymentMethod,
      )
      .subscribe((response) => {
        this.transactions = response.results;
        this.nextPageUrl = response.next;
        this.previousPageUrl = response.previous;
        this.currentPage = page;

        const itemsPerPage = 20;
        this.totalPages = Math.ceil(response.count / itemsPerPage);
        this.pageNumbers = generatePagination(
          this.currentPage,
          this.totalPages,
        );
        this.submitted = false;
      });
  }

  goToNextPage() {
    if (this.nextPageUrl) {
      const nextPage = this.getPageNumber(this.nextPageUrl);
      this.loadTransactions(nextPage);
    }
  }

  goToPreviousPage() {
    if (this.previousPageUrl) {
      const prevPage = this.getPageNumber(this.previousPageUrl);
      this.loadTransactions(prevPage);
    }
  }

  private getPageNumber(url: string): number {
    if (!url) return 1; // fallback if url is null

    const queryString = url.split('?')[1]; // get everything after "?"
    if (!queryString) return 1;

    const params = new URLSearchParams(queryString);
    const page = params.get('page');

    return page ? Number(page) : 1;
  }

  onSearchChange() {
    this.loadTransactions(1);
  }

  onSelectionChange() {
    this.loadTransactions(1);
  }

  onActiveRfiChange() {
    this.loadTransactions(1);
  }

  clearForm() {
    this.transactionStatus = '';
    this.transactionType = '';
    this.accountingType = '';
    this.paymentMethod = '';
    this.activeRfi = false;

    this.loadTransactions(1);
  }

  openRefundModal(transaction: Transaction) {
    this.selectedTransaction = transaction;
    this.showRefundModal = true;
  }

  openReturnModal(transaction: Transaction) {
    this.selectedTransaction = transaction;
    this.showReturnModal = true;
  }

  handleRefund(event: {
    uuid: string;
    type: 'full' | 'partial';
    amount?: number;
  }) {
    this.isRefunding = true;

    this.debtService
      .refundTransaction(event.uuid, event.type, event.amount)
      .subscribe({
        next: () => {
          alert('Transaction has been successfully refunded.');
          this.isRefunding = false;
          this.showRefundModal = false;
          this.transactionService.invalidateTransactionsCache();
          this.loadTransactions(this.currentPage);
        },
        error: () => {
          alert('Failed to refund transaction.');
          this.isRefunding = false;
        },
      });
  }

  handleReturn(event: ReturnTransactionPayload & { uuid: string }) {
    const { uuid, ...payload } = event;
    this.debtService
      .returnTransaction(uuid, payload)
      .subscribe({
        next: () => {
          alert('Transaction has been successfully returned.');
          this.transactionService.invalidateTransactionsCache();
          this.loadTransactions(this.currentPage);
        },
        error: (err) => {
          console.error('Failed to return transaction:', err);
          alert(err?.error?.message || 'Failed to return transaction.');
        },
      });

    this.showReturnModal = false;
  }

  canReturnTransaction(transaction: Transaction): boolean {
    if (!this.isComplianceAdmin || !transaction || transaction.returned_at) {
      return false;
    }

    // Must be CREDIT
    if (transaction.accounting_type !== 'CREDIT') {
      return false;
    }

    // Noah does not support returns
    if (transaction.noah_id) {
      return false;
    }

    const pm = (transaction.payment_method || '').toUpperCase();
    const tt = (transaction.transaction_type || '').toUpperCase();
    const st = (transaction.service_type || '').toUpperCase();

    // 1. Credit bank transfer
    const isBankTransfer =
      tt === 'BANK_TRANSFER' ||
      ['SEPA', 'ACH', 'WIRE', 'BANK_TRANSFER'].includes(pm);

    // 2. Credit Crypto deposit
    const isCryptoDeposit =
      tt === 'CRYPTO_PAYMENT' ||
      tt === 'CRYPTO_DEPOSIT' ||
      pm === 'CRYPTO' ||
      st === 'CRYPTO' ||
      (tt === 'TOPUP' && pm === 'CRYPTO');

    return Boolean(isBankTransfer || isCryptoDeposit);
  }

  checkPrivileges() {
    this.isComplianceAdmin = this.authService.isRefundsManager();
    this.isRefundsManager = this.authService.isRefundsManager();
  }

  openTransferModal() {
    this.transferSourceWallet = '';
    this.transferAmount = null;
    this.transferReference = '';
    this.relatedTransactionUuid = '';
    this.relatedTransactionType = '';
    this.showTransferModal = true;
  }

  closeTransferModal() {
    this.showTransferModal = false;
    this.transferSourceWallet = '';
    this.transferAmount = null;
    this.transferReference = '';
    this.relatedTransactionUuid = '';
    this.relatedTransactionType = '';
  }

  truncateHash(hash: string): string {
    if (!hash || hash.length <= 8) return hash;
    return `${hash.slice(0, 4)}...${hash.slice(-4)}`;
  }

  confirmTransfer() {
    if (
      !this.transferSourceWallet ||
      this.transferAmount === null ||
      !this.transferReference
    )
      return;

    // If related_transaction_uuid is provided, related_transaction_type is required
    if (this.relatedTransactionUuid && !this.relatedTransactionType) {
      alert(
        'Related Transaction Type is required when Related Transaction UUID is provided.',
      );
      return;
    }

    this.isTransferring = true;

    const payload: Record<string, any> = {
      source_wallet: this.transferSourceWallet,
      amount: this.transferAmount,
      reference: this.transferReference,
    };

    if (this.relatedTransactionUuid) {
      payload['related_transaction_uuid'] = this.relatedTransactionUuid;
      payload['related_transaction_type'] = this.relatedTransactionType;
    }

    this.userService.transferFundsToUser(this.uuid, payload).subscribe({
      next: () => {
        alert('Funds transferred successfully.');
        this.closeTransferModal();
        this.loadTransactions(this.currentPage);
      },
      error: () => {
        alert('Failed to transfer funds.');
        this.isTransferring = false;
      },
      complete: () => {
        this.isTransferring = false;
      },
    });
  }

  getEntry(transaction: Transaction, type: 'DEBIT' | 'CREDIT') {
    return transaction.entries?.find((e) => e.accounting_type === type);
  }

  isWaitingForRfi(transaction: Transaction): boolean {
    return Boolean(transaction?.rfi_received_at) && !transaction?.rfi_completed_at && !transaction?.rfi_failed_at;
  }
}
